import { DOC_KINDS, ITEM_KINDS, ITEM_PROPS, type ItemProp } from '@epc/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { QrCode } from 'lucide-react';
import { useState } from 'react';
import { HistoryButton } from '../../components/History';
import { useCrumbs } from '../../layout/crumbs';
import { dateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { KeyValue, Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { ItemForm, ReservationDialog } from './forms';
import { ItemPreservationPanel } from './PreservationPanel';
import { QuickCount } from './QuickCount';
import { itemQuery, movementsQuery, qty, reservationsQuery, stockQuery } from './queries';
import { StockTable } from './StockTable';

/** Карточка позиции каталога: остатки по складам, резервы, журнал движений, этикетка (СК-02, СК-10) */
export function ItemPage() {
  const { itemId } = useParams({ strict: false }) as { itemId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = useCan('warehouse.operate');
  const q = useQuery(itemQuery(itemId));
  const stock = useQuery(stockQuery({ itemId }));
  const res = useQuery(reservationsQuery({ itemId }));
  const mv = useQuery(movementsQuery({ itemId, limit: 100 }));
  const [editing, setEditing] = useState(false);
  const [reserving, setReserving] = useState(false);
  const it = q.data;
  useCrumbs(
    it
      ? [
          { label: 'Каталог ТМЦ', to: '/warehouse?tab=items' },
          { label: it.code, title: it.name },
        ]
      : null,
    q.error,
  );
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError || !it)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const rows = stock.data ?? [];
  const inQc = rows.reduce((s, r) => s + r.inQc, 0);
  const reserved = rows.reduce((s, r) => s + r.reserved, 0);
  return (
    <Page>
      <PageHeader
        title={it.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="num">{it.code}</span>
            <Badge tone={it.kind === 'tool' ? 'info' : 'neutral'}>{ITEM_KINDS[it.kind]}</Badge>
            {!it.isActive ? <Badge tone="neutral">закрыта</Badge> : null}
            {it.certRequired ? <Badge tone="warning">нужен сертификат</Badge> : null}
            {it.passportRequired ? <Badge tone="warning">нужен паспорт</Badge> : null}
          </span>
        }
        actions={
          <>
            <Button
              icon={<QrCode className="size-4" />}
              onClick={() => navigate({ to: '/warehouse/labels', search: { items: it.id } })}
            >
              Этикетка
            </Button>
            {canEdit ? <Button onClick={() => setReserving(true)}>Резерв</Button> : null}
            {canEdit ? (
              <Button variant="primary" onClick={() => setEditing(true)}>
                Править
              </Button>
            ) : null}
            <HistoryButton table="catalog_items" rowId={it.id} title={it.code} />
          </>
        }
      />
      <TitleBlock
        className="mb-6"
        items={[
          { label: `Остаток, ${it.unit}`, value: qty(it.onHand), note: 'по складам, которые вам открыты' },
          { label: 'На входном контроле', value: qty(inQc), note: 'не выдаётся до решения ОТК' },
          { label: 'В резерве', value: qty(reserved) },
          { label: 'Доступно', value: qty(it.available), tone: it.available > 0 ? 'success' : 'default' },
        ]}
      />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-6">
          <Panel title="Остатки по складам" bodyClassName="p-0">
            {rows.length ? (
              <>
                <StockTable rows={rows} hide="item" />
                <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-line px-5 py-2.5 text-[13px]">
                  <span className="text-ink-2">Карточка учёта запасов З-3:</span>
                  {rows.map((r) => (
                    <a
                      key={r.warehouseId}
                      href={`/api/v1/warehouse/items/${it.id}/card.html?warehouseId=${r.warehouseId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-11 items-center hover:underline"
                    >
                      {r.warehouseName}
                    </a>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-5">
                <EmptyState title="На складах нет">
                  Позиция появится в остатках после проведения поступления.
                </EmptyState>
              </div>
            )}
          </Panel>
          <Panel title="Журнал движений" meta="последние 100" bodyClassName="p-0">
            {mv.data?.length ? (
              <ScrollX>
                <table className="w-full min-w-[640px] text-[14px]">
                  <thead>
                    <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                      <th className="py-2.5 pr-3 pl-5 font-medium">Когда</th>
                      <th className="py-2.5 pr-3 font-medium">Документ</th>
                      <th className="py-2.5 pr-3 font-medium">Склад / получатель</th>
                      <th className="py-2.5 pr-5 text-right font-medium">Количество</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mv.data.map((m) => (
                      <tr key={m.id} className="border-t border-line">
                        <td className="num py-2.5 pr-3 pl-5 text-ink-2">{dateTime(m.at)}</td>
                        <td className="py-2.5 pr-3">
                          <Link
                            to={`/warehouse/documents/${m.documentId}`}
                            className="font-medium hover:underline"
                          >
                            {m.documentNumber}
                          </Link>
                          <div className="text-[12px] text-ink-2">{DOC_KINDS[m.documentKind].title}</div>
                        </td>
                        <td className="py-2.5 pr-3">
                          {m.warehouseName ?? m.holderName ?? '—'}
                          {m.state === 'qc' ? (
                            <span className="ml-2 text-[12px] text-warning">контроль</span>
                          ) : null}
                          {m.batch ? <div className="text-[12px] text-ink-2">партия {m.batch}</div> : null}
                        </td>
                        <td
                          className={`num py-2.5 pr-5 text-right ${m.quantity < 0 ? 'text-danger' : 'text-success'}`}
                        >
                          {m.quantity > 0 ? '+' : ''}
                          {qty(m.quantity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
            ) : (
              <p className="p-5 text-[14px] text-ink-2">Движений пока не было.</p>
            )}
          </Panel>
        </div>
        <div className="flex flex-col gap-6">
          {canEdit ? <QuickCount itemId={it.id} /> : null}
          <Panel title="Карточка">
            <KeyValue
              items={[
                { label: 'Единица учёта', value: it.unit },
                ...(it.convUnit
                  ? [{ label: 'Пересчёт', value: `1 ${it.unit} = ${qty(it.convFactor)} ${it.convUnit}` }]
                  : []),
                { label: 'Класс АГСК-3', value: it.agskClass ?? '—' },
                ...(Object.keys(ITEM_PROPS) as ItemProp[])
                  .filter((k) => it.props[k])
                  .map((k) => ({ label: ITEM_PROPS[k], value: it.props[k] })),
                ...(it.shelfLifeDays ? [{ label: 'Срок годности', value: `${it.shelfLifeDays} дн.` }] : []),
                ...(it.minStock !== null ? [{ label: 'Неснижаемый запас', value: qty(it.minStock) }] : []),
                ...(it.targetStock !== null ? [{ label: 'Целевой запас', value: qty(it.targetStock) }] : []),
                ...(it.note ? [{ label: 'Примечание', value: it.note }] : []),
              ]}
            />
          </Panel>
          {it.kind === 'tool' ? <ItemPreservationPanel itemId={it.id} /> : null}
          <Panel title="Резервы" meta={res.data?.length ? String(res.data.length) : undefined}>
            {res.data?.length ? (
              <ul className="flex flex-col gap-2 text-[14px]">
                {res.data.map((r) => (
                  <li key={r.id}>
                    <span className="num font-medium">
                      {qty(r.quantity)} {r.unit}
                    </span>{' '}
                    на складе {r.warehouseName}
                    {r.workRef ? ` · работа ${r.workRef}` : ''}
                    {r.status === 'requested' ? ' · ждёт согласия владельца склада' : ''}
                    {r.status === 'declined' ? ' · отказано' : ''}
                    <div className="text-[12px] text-ink-2">
                      {r.reason} — {r.createdByName ?? '—'}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[14px] text-ink-2">Резервов нет.</p>
            )}
          </Panel>
        </div>
      </div>
      {editing ? (
        <Dialog open onOpenChange={(v) => !v && setEditing(false)} title={`Позиция ${it.code}`} width="lg">
          <ItemForm
            initial={it}
            onCancel={() => setEditing(false)}
            onSaved={(i) => {
              qc.setQueryData(itemQuery(itemId).queryKey, i);
              setEditing(false);
            }}
          />
        </Dialog>
      ) : null}
      {reserving ? (
        <ReservationDialog open onOpenChange={(v) => !v && setReserving(false)} presetItemId={it.id} />
      ) : null}
    </Page>
  );
}
